import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PermissionEnum, Currency, Response, SelectItemProps } from '@ui/src';
import { RateSource, TariffClientType } from '../types';
import { exchangeComponentApi, PermissionResponse } from './services';

export interface ExchangeDictionariesState {
  activePermissions?: PermissionEnum[];
  currencies?: Currency[];
  rateSource?: RateSource[];
  tariffs?: SelectItemProps[];
  riskScoreRanges?: SelectItemProps[];
  whitelabel?: {
    data: {
      value: Record<string, string>;
    };
  };
}

const initialState: ExchangeDictionariesState = {};

export const exchangeDictionariesSlice = createSlice({
  name: 'exchangeDictionaries',
  initialState,
  reducers: {
    setActivePermissions: (state, action: PayloadAction<PermissionEnum[]>) => {
      state.activePermissions = action.payload;
    },
    setCurrenciesExchangeRateSources: (
      state,
      action: PayloadAction<RateSource[]>,
    ) => {
      state.rateSource = action.payload.map((item) => item);
    },
    setCurrenciesExchangeCurrencies: (
      state,
      action: PayloadAction<Currency[]>,
    ) => {
      state.currencies = action.payload.map((item) => item);
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      exchangeComponentApi.endpoints.getExchangePermissions.matchFulfilled,
      (state, action: PayloadAction<{ data: PermissionResponse }>) => {
        state.activePermissions = action.payload.data.permissions;

        localStorage.setItem(
          'activePermissions',
          JSON.stringify(action.payload.data.permissions),
        );
      },
    );
    builder.addMatcher(
      exchangeComponentApi.endpoints.getCurrenciesExchangeCurrencies
        .matchFulfilled,
      (state, action: PayloadAction<Response<Currency>>) => {
        state.currencies = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      exchangeComponentApi.endpoints.getCurrenciesExchangeRateSources
        .matchFulfilled,
      (state, action: PayloadAction<Response<RateSource>>) => {
        state.rateSource = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder
      .addMatcher(
        exchangeComponentApi.endpoints.getExchangeDictionaries.matchFulfilled,
        (
          state,
          action: PayloadAction<{
            tariffs: Response<{
              id: string;
              name: string;
              type: TariffClientType;
            }>;
            riskScoreRanges: Response<{
              id: string;
              name: string;
              rangeStart: number;
              rangeEnd: number;
            }>;
          }>,
        ) => {
          state.tariffs = action?.payload?.tariffs?.data.edges.map((item) => ({
            name: `${item.node.name} (${
              item.node.type === TariffClientType.INDIVIDUAL
                ? 'Individual'
                : 'Corporate'
            })`,
            value: item.node.id,
          }));

          state.riskScoreRanges = action.payload.riskScoreRanges.data.edges.map(
            (item) => ({
              name: item.node.name,
              value: item.node.id,
              description: `${item.node.rangeStart} - ${item.node.rangeEnd}`,
            }),
          );
        },
      )
      .addMatcher(
        exchangeComponentApi.endpoints.getExchangeWhitelabel.matchFulfilled,
        (
          state,
          action: PayloadAction<{
            data: {
              data: {
                value: Record<string, string>;
              };
            };
          }>,
        ) => {
          state.whitelabel = action.payload.data;
        },
      );
  },
});

export const {
  setActivePermissions,
  setCurrenciesExchangeCurrencies,
  setCurrenciesExchangeRateSources,
} = exchangeDictionariesSlice.actions;

export default exchangeDictionariesSlice.reducer;
